import { useMutation } from '@apollo/client'
import { Icon, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isMobile } from 'react-device-detect'
import { Button } from '@/components'
import { RESET_PASSWORD_MUTATION } from '@/modules/login/mutations'
import type {
  ResetPasswordMutation,
  ResetPasswordMutationVariables,
} from '@/modules/login/types.graphql'
import { useAdmin, useConfirm, useOnErrorAuto } from '@/utils/hooks'

interface Props {
  email: string
  fullName: string
}

export const SendResetPasswordButton: React.FC<Props> = ({
  email,
  fullName,
}) => {
  const translations = useTranslate({
    sendResetPassword: 'users.send-reset-password',
    resetPasswordConfirm: ['users.reset-password-confirm', { name: '' }],
    resetPasswordConfirmTitle: 'users.reset-password-confirm-title',
    resetPasswordSent: 'users.reset-password-sent',
  })

  const admin = useAdmin()
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()
  const confirm = useConfirm()

  const [resetPassword, { loading }] = useMutation<
    ResetPasswordMutation,
    ResetPasswordMutationVariables
  >(RESET_PASSWORD_MUTATION, {
    onCompleted: ({ resetMyPassword: { ok } }) => {
      if (ok) addToast('success', translations.resetPasswordSent)
    },
    onError: onErrorAuto(),
  })

  const handleClick = async () => {
    const { data: answer } = await confirm(
      translations.resetPasswordConfirm({ name: fullName }),
      translations.resetPasswordConfirmTitle
    )
    if (!answer) return
    void resetPassword({ variables: { username: email } })
  }

  if (!admin) return null

  return (
    <Button
      variant="outline"
      size={isMobile ? 'icon' : 'default'}
      disabled={loading}
      onClick={handleClick}
      title={translations.sendResetPassword}
      className="whitespace-nowrap"
    >
      {isMobile ? (
        <Icon icon="key" />
      ) : (
        <span className="flex items-center gap-2">
          {translations.sendResetPassword}
        </span>
      )}
    </Button>
  )
}
